'use client';

import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Home } from 'lucide-react';
import dynamic from 'next/dynamic';
import { RootState, AppDispatch } from '@/app/store';
import { setActiveTenant, ActiveTenant } from '@/app/store/slices/tenantSlice';
import { fetchUserTenants } from '@/app/store/thunks/tenantThunks';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import { useWorkspaceRouter } from '@/app/features/workspace/routing/useWorkspaceRouter';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TenantPickerModal = dynamic(
  () => import('@/app/features/workspace/components/TenantPickerModal'),
  { ssr: false }
);

interface TenantSwitcherProps {
  className?: string;
  /** 'header' = subtle read-only indicator (multi-tenant only); 'settings' = full dropdown */
  variant?: 'header' | 'settings';
  /** Controlled mode: current tenant id value (settings page uses this for pending state) */
  value?: string;
  /** Controlled mode: called when user picks a different tenant (no Redux dispatch) */
  onChange?: (tenantId: string) => void;
  /** Optional tenant list override — settings page passes user-scoped tenants instead of Redux global list */
  tenants?: import('@/app/services/api/tenantService').Tenant[];
}

export function TenantSwitcher({ className, variant = 'settings', value, onChange, tenants: tenantsProp }: TenantSwitcherProps) {
  const dispatch = useDispatch<AppDispatch>();
  const router = useWorkspaceRouter();
  const reduxTenants = useSelector((state: RootState) => state.tenant.tenants);
  const userTenants = useSelector((state: RootState) => state.tenant.userTenants);
  const activeTenant = useSelector((state: RootState) => state.tenant.activeTenant);
  const isLoading = useSelector((state: RootState) => state.tenant.isLoading);
  const userTenantCount = useSelector((state: RootState) => state.tenant.userTenantCount);
  const userTenantsLoaded = useSelector((state: RootState) => state.tenant.userTenantsLoaded);
  const authUserId = useSelector((state: RootState) => state.auth.user?.id);
  const isControlled = value !== undefined && onChange !== undefined;
  const { t } = useTranslation('settings');
  const [pickerOpen, setPickerOpen] = useState(false);
  // Click queued before `fetchUserTenants` resolves — resolved by the effect
  // below as soon as `userTenantsLoaded` flips true, so the user's intent
  // survives the brief load window without misrouting.
  const [pendingHeaderClick, setPendingHeaderClick] = useState(false);

  // Single-tenant shortcut: ensure that tenant is active, then jump to its
  // tenant-coded workspace (spec: workspace-tenant-code-routing).
  const handleSingleTenantJump = useCallback(() => {
    const only = userTenants[0];
    if (!only) return;
    if (activeTenant?.tenantId !== String(only.id)) {
      dispatch(setActiveTenant({
        tenantId: String(only.id),
        tenantCode: only.tenantCode,
        tenantName: only.tenantName,
        configurationJson: only.configurationJson ?? null,
      }));
    }
    router.push(workspaceHref(only));
  }, [userTenants, activeTenant, dispatch, router]);

  // Resolve the header shortcut click against the membership count — same
  // branching as the post-load JSX below (0 → /main, 1 → workspace, 2+ →
  // picker). Used both for immediate clicks and to drain a queued pre-load
  // click once `fetchUserTenants` resolves.
  const resolveHeaderNavigation = useCallback(() => {
    if (userTenantCount === 0) {
      router.push('/main');
      return;
    }
    if (userTenantCount === 1) {
      handleSingleTenantJump();
      return;
    }
    setPickerOpen(true);
  }, [userTenantCount, router, handleSingleTenantJump]);

  // Drain a queued pre-load click. The header button stays clickable during
  // the brief `userTenantsLoaded === false` window so it never visually
  // disappears; the click is parked and executed here once the membership
  // count is authoritative, so a multi-tenant user always sees the picker
  // and a single-tenant user lands in their workspace — never the wrong
  // count-zero `/main` fallback.
  useEffect(() => {
    if (pendingHeaderClick && userTenantsLoaded) {
      setPendingHeaderClick(false);
      resolveHeaderNavigation();
    }
  }, [pendingHeaderClick, userTenantsLoaded, resolveHeaderNavigation]);

  // Use prop-supplied list (user-scoped) when provided, otherwise fall back to Redux global list
  const tenants = tenantsProp ?? reduxTenants;

  if (isLoading) {
    if (variant === 'header') return null; // don't show skeleton in header
    return <div className={`h-8 w-48 rounded bg-muted animate-pulse ${className ?? ''}`} />;
  }

  // Header variant: persistent workspace shortcut in the top bar.
  // Always renders the Home icon so the affordance reads as a single stable
  // "workspace" button regardless of state (the active tenant's identity is
  // shown separately in the upper-left corner of the header).
  //   • 0 tenants            → /main (public apps landing)
  //   • 1 tenant             → jump straight to /workspace
  //   • 2+ tenants           → opens the tenant picker
  if (variant === 'header') {
    // Icon-only 32x32 circle matches Avatar and the other uniform-width icon
    // buttons in the header (LanguageSelector / ThemeCustomizer / NotificationBell
    // are all h-8 w-8). Keeps gap-to-center distances uniform across the cluster.
    const iconBtn = `inline-flex items-center justify-center h-8 w-8 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors ${className ?? ''}`;

    // Pre-load: keep the icon visible (so the header cluster doesn't shift
    // and the user can see they're in the right place), but park the click
    // until `fetchUserTenants` resolves. The drain effect above runs the
    // real branching once the count is known, so a hard-refresh + fast
    // click on an anonymous route (/welcome/{code}) never misroutes — a
    // single-tenant user lands in their workspace, a multi-tenant user
    // gets the picker, and a no-tenant user goes to /main. We also
    // defensively dispatch `fetchUserTenants` from the click — TenantLoader
    // normally fires this, but kicking it off here too guarantees the
    // pending click eventually drains even on routes where the loader race
    // skipped the dispatch.
    if (!userTenantsLoaded) {
      const ariaBusy = pendingHeaderClick ? true : undefined;
      const handlePreloadClick = () => {
        const uid = authUserId ? parseInt(authUserId, 10) : NaN;
        if (Number.isFinite(uid) && uid > 0) {
          dispatch(fetchUserTenants(uid));
        }
        setPendingHeaderClick(true);
      };
      return (
        <button
          type="button"
          onClick={handlePreloadClick}
          title={t('activeTenant.goToWorkspace')}
          aria-label={t('activeTenant.goToWorkspace')}
          aria-busy={ariaBusy}
          className={iconBtn}
        >
          <Home size={16} className="shrink-0" />
        </button>
      );
    }

    if (userTenantCount === 0) {
      return (
        <button
          type="button"
          onClick={() => router.push('/main')}
          title={t('activeTenant.goToWorkspace')}
          aria-label={t('activeTenant.goToWorkspace')}
          className={iconBtn}
        >
          <Home size={16} className="shrink-0" />
        </button>
      );
    }

    if (userTenantCount === 1) {
      return (
        <button
          type="button"
          onClick={handleSingleTenantJump}
          title={t('activeTenant.goToWorkspace')}
          aria-label={t('activeTenant.goToWorkspace')}
          className={iconBtn}
        >
          <Home size={16} className="shrink-0" />
        </button>
      );
    }

    // userTenantCount >= 2
    if (!activeTenant) {
      return (
        <>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            title={t('activeTenant.goToWorkspace')}
            aria-label={t('activeTenant.goToWorkspace')}
            className={iconBtn}
          >
            <Home size={16} className="shrink-0" />
          </button>
          <TenantPickerModal
            open={pickerOpen}
            onDismiss={() => setPickerOpen(false)}
          />
        </>
      );
    }

    // 2+ tenants with an active tenant: consistent Home icon (the active
    // tenant's identity is shown in the header upper-left corner, so we keep
    // a single workspace-shortcut affordance regardless of state). The tenant
    // name stays available on hover via `title`.
    return (
      <>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          title={activeTenant.tenantName}
          aria-label="Switch tenant"
          className={iconBtn}
        >
          <Home size={16} className="shrink-0" />
        </button>
        <TenantPickerModal
          open={pickerOpen}
          onDismiss={() => setPickerOpen(false)}
        />
      </>
    );
  }

  // Settings variant: always show (even with 0 or 1 tenant). An active tenant
  // is required (spec: workspace-tenant-code-routing) — there is no "No tenant"
  // option; the user can only switch between the tenants they belong to.
  const handleValueChange = (raw: string) => {
    if (isControlled) {
      onChange!(raw);
      return;
    }
    // Uncontrolled mode: dispatch immediately.
    const selected = (tenants ?? []).find((t) => String(t.id) === raw);
    if (!selected) return;
    const payload: ActiveTenant = {
      tenantId: String(selected.id),
      tenantCode: selected.tenantCode,
      tenantName: selected.tenantName,
    };
    dispatch(setActiveTenant(payload));
  };

  // Empty string renders the placeholder (no item matches it) — the no-active-
  // tenant state is transitional; the user is expected to pick a real tenant.
  const selectValue = (isControlled ? value : activeTenant?.tenantId) ?? '';

  return (
    <Select value={selectValue} onValueChange={handleValueChange}>
      <SelectTrigger className={`w-full ${className ?? ''}`} aria-label="Switch tenant">
        <SelectValue placeholder={t('activeTenant.noTenant')} />
      </SelectTrigger>
      <SelectContent>
        {(tenants ?? []).map((tn) => (
          <SelectItem key={tn.id} value={String(tn.id)}>
            {tn.tenantName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default TenantSwitcher;
